import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Search, Loader2, ImageOff, ExternalLink } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

// Core image-search UI (input + results grid + lightbox). Used by the global page
// and the modal dialog. When customerId is set, search is scoped to that customer.
export const ImageSearchContent = ({ customerId, customerName, onNavigate }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const runSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (customerId) params.customer_id = customerId;
      const res = await api.get('/embedded-images/search', { params });
      setResults(res.data?.results || []);
      setCount(res.data?.count || 0);
      setSearched(true);
    } catch (e) {
      setResults([]);
      setCount(0);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // Load recent images on first mount.
  useEffect(() => { runSearch(''); }, [runSearch]);

  const goToTicket = (id) => {
    if (onNavigate) onNavigate();
    navigate(`/tickets/${id}`);
  };

  return (
    <div className="space-y-4">
      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(query.trim()); }}
        className="flex gap-2"
      >
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            data-testid="image-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={customerName ? `Search ${customerName}'s screenshots by filename or ticket…` : 'Search screenshots by filename or ticket number/subject…'}
            className="pl-9"
          />
        </div>
        <Button type="submit" data-testid="image-search-submit" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
        </Button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : results.length === 0 ? (
        <div data-testid="image-search-empty" className="flex flex-col items-center justify-center py-12 text-gray-400">
          <ImageOff className="h-8 w-8 mb-2" />
          <p className="text-sm">{searched ? 'No matching ticket images' : 'Search ticket image attachments'}</p>
          <p className="text-xs mt-1 text-gray-500 max-w-md text-center">
            {searched
              ? 'Try a different file name, or search by a ticket number or subject keyword. Only images attached to tickets are searched.'
              : 'Enter a file name, ticket number, or subject keyword to find screenshots and images attached to tickets.'}
          </p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500" data-testid="image-search-count">
            Showing {results.length}{count > results.length ? ` of ${count}` : ''} image{results.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 items-start">
            {results.map((r, i) => (
              <div key={i} data-testid="image-search-result" className="border rounded-md overflow-hidden bg-gray-50">
                <div className="relative">
                  <img
                    src={r.src}
                    alt={r.name}
                    onClick={() => setLightbox({ images: results.map((x) => ({ src: x.src, name: x.name })), index: i })}
                    className="w-full h-28 object-contain bg-white cursor-zoom-in"
                  />
                  {r.is_signature && (
                    <span className="absolute top-1 left-1 rounded bg-black/55 text-white text-[10px] px-1.5 py-0.5">logo</span>
                  )}
                </div>
                <div className="px-2 py-1.5 text-xs bg-[#081a25] border-t space-y-1">
                  <p className="font-medium text-gray-700 truncate" title={r.name}>{r.name}</p>
                  <button
                    type="button"
                    data-testid="image-search-ticket-link"
                    onClick={() => goToTicket(r.ticket_id)}
                    className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 truncate max-w-full"
                    title={`${r.ticket_number} · ${r.ticket_title || ''}`}
                  >
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate">{r.ticket_number}</span>
                  </button>
                  {!customerId && r.customer_name && (
                    <p className="text-gray-400 truncate">{r.customer_name}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {lightbox && (
        <ImageLightbox images={lightbox.images} startIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
};

export default ImageSearchContent;
